/**
 * Error classes and type-checking functions for KaiChat module
 */

export class UserFacingError extends Error {
  constructor(
    message: string,
    public retry = true
  ) {
    super(message)
    this.name = "UserFacingError"
  }
}

export class ToolCallError extends Error {
  constructor(
    message: string,
    public toolName: string,
    public toolCallId: string
  ) {
    super(message)
    this.name = "ToolCallError"
  }
}

export class StreamError extends Error {
  constructor(
    message: string,
    public reason?: string,
    public nativeError?: Error
  ) {
    super(message)
    this.name = "StreamError"
  }
}

export class AbortError extends Error {
  constructor(message = "Request was aborted") {
    super(message)
    this.name = "AbortError"
  }
}

/**
 * Type guards for error checking
 */
export const isUserFacingError = (error: unknown): error is UserFacingError => {
  return error instanceof UserFacingError
}

export const isToolCallError = (error: unknown): error is ToolCallError => {
  return error instanceof ToolCallError
}

export const isStreamError = (error: unknown): error is StreamError => {
  return error instanceof StreamError
}

export const isAbortError = (error: unknown): error is AbortError => {
  return (
    error instanceof AbortError ||
    (error instanceof DOMException && error.name === "AbortError")
  )
}

/**
 * Error factory functions
 */
export const createUserFacingError = (
  message: string,
  retry = true
): UserFacingError => {
  return new UserFacingError(message, retry)
}

export const createToolCallError = (
  message: string,
  toolName: string,
  toolCallId: string
): ToolCallError => {
  return new ToolCallError(message, toolName, toolCallId)
}

export const createStreamError = (
  message: string,
  reason?: string,
  nativeError?: Error
): StreamError => {
  return new StreamError(message, reason, nativeError)
}

export const createAbortError = (message?: string): AbortError => {
  return new AbortError(message)
}
